import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import Select from "react-select";
import { toast, ToastContainer } from "react-toastify";
import "react-datepicker/dist/react-datepicker.css";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../../config";

const emptyRow = {
  emp_id: "",
  overtime_date: new Date(),
  in_time: "",
  out_time: "",
  hours: "",
  remark: "",
};

const ManualOT = () => {
  const [employees, setEmployees] = useState([]);
  const [rows, setRows] = useState([{ ...emptyRow }]);
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  /* =============================
     FETCH EMPLOYEES
  ==============================*/
  useEffect(() => {
    axios
      .get(API_BASE_URL + "/all_employees", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })
      .then((res) => {
        setEmployees(res.data?.data?.all_employee || []);
      })
      .catch(() => toast.error("Failed to load employees"));
  }, []);

  /* =============================
     CALCULATE OT HOURS
  ==============================*/
  const calculateHours = (inTime, outTime) => {
    if (!inTime || !outTime) return "";

    const start = new Date(`1970-01-01T${inTime}`);
    const end = new Date(`1970-01-01T${outTime}`);

    let diff = (end - start) / 1000 / 60;
    if (diff < 0) diff += 1440;

    const hrs = Math.floor(diff / 60);
    const mins = diff % 60;

    return `${hrs}:${String(mins).padStart(2, "0")}`;
  };

  /* =============================
     UPDATE ROW
  ==============================*/
  const updateRow = (index, key, value) => {
    const updated = [...rows];
    updated[index][key] = value;

    if (key === "in_time" || key === "out_time") {
      updated[index].hours = calculateHours(
        updated[index].in_time,
        updated[index].out_time
      );
    }

    setRows(updated);
  };

  /* =============================
     ADD / REMOVE ROW
  ==============================*/
  const addRow = () => {
    setRows([...rows, { ...emptyRow }]);
  };

  const removeRow = (index) => {
    if (rows.length === 1) return;
    setRows(rows.filter((_, i) => i !== index));
  };

  /* =============================
     FORM VALIDATION
  ==============================*/
  const isFormValid = rows.every(
    (r) => r.emp_id && r.in_time && r.out_time && r.hours && r.remark
  );

  /* =============================
     SUBMIT
  ==============================*/
  const submitOT = (e) => {
    e.preventDefault();

    // block repeat clicks while a save request is already running
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);

    axios
      .post(
        API_BASE_URL + "/add_manualot",
        { rows },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        }
      )
      .then((res) => {
        toast.success(res.data.message);
        setRows([{ ...emptyRow }]);
      })
      .catch((error) => {
        toast.error(
          error.response?.data?.message || "Request failed",
          { toastId: "manualot-error" }
        );
      })
      .finally(() => {
        savingRef.current = false;
        setSaving(false);
      });
  };

  return (
    <>
      <ToastContainer position="bottom-right" theme="colored" />

      <form onSubmit={submitOT}>
        <div className="card">
          {/* HEADER */}
          <div className="card-header d-flex justify-content-between align-items-center">
            <h3 className="card-title d-flex align-items-center mb-0" style={{ color: "#1d3a8a", fontWeight: 600 }}>
        <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
          <path d="M8 3.5a.5.5 0 0 0-1 0V9a.5.5 0 0 0 .252.434l3.5 2a.5.5 0 0 0 .496-.868L8 8.71z"/><path d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16m7-8A7 7 0 1 1 1 8a7 7 0 0 1 14 0"/>
        </svg>
        Manual OT
      </h3>
          </div>

          {/* BODY */}
          <div className="card-body">
            {rows.map((row, index) => (
              <div
                className="row align-items-end g-2 mb-3 border-bottom pb-3"
                key={index}
              >
                {/* EMPLOYEE */}
                <div
                  className="col-md-2"
                  title={
                    employees.find((e) => e.value === row.emp_id)?.label ||
                    "No employee selected"
                  }
                >
                  <label className="form-label small">Employee</label>
                  <Select classNames={{
    control: () => "required-select required-tooltip"
  }}
                    options={employees}
                    placeholder="Select"
                    value={
                      employees.find((e) => e.value === row.emp_id) || null
                    }
                    onChange={(opt) =>
                      updateRow(index, "emp_id", opt?.value)
                    }
                  />
                </div>

                {/* DATE */}
                <div className="col-md-2">
                  <label className="form-label small">OT Date</label>
                  <DatePicker
                    selected={row.overtime_date}
                    onChange={(date) =>
                      updateRow(index, "overtime_date", date)
                    }
                    className="form-control required-input"
                    dateFormat="yyyy-MM-dd"
                    maxDate={new Date()}
                  />
                </div>

                {/* IN TIME */}
                <div className="col-md-2">
                  <label className="form-label small">In Time</label>
                  <input
                    type="time"
                    step="60"
                    className="form-control clean-time"
                    value={row.in_time || ""}
                    onChange={(e) =>
                      updateRow(index, "in_time", e.target.value)
                    }
                  />
                </div>

                {/* OUT TIME */}
                <div className="col-md-2">
                  <label className="form-label small">Out Time</label>
                  <input
                    type="time"
                    step="60"
                    className="form-control clean-time"
                    value={row.out_time || ""}
                    disabled={!row.in_time}
                    onChange={(e) =>
                      updateRow(index, "out_time", e.target.value)
                    }
                  />
                </div>

                {/* HOURS */}
                <div className="col-md-1">
                  <label className="form-label small">Hours</label>
                  <input
                    className="form-control bg-light text-center"
                    value={row.hours}
                    readOnly
                  />
                </div>

                {/* REASON */}
                <div className="col-md-2">
                  <label className="form-label small">Reason</label>
                  <input
                    className="form-control required-input"
                    placeholder="Reason"
                    value={row.remark}
                    disabled={!row.hours}
                    onChange={(e) =>
                      updateRow(index, "remark", e.target.value)
                    }
                  />
                </div>

                {/* ACTION (+ and × together) */}
               {/* ACTION */}
{/* ACTION */}
<div
  className="col-md-1 d-flex justify-content-center"
  style={{ gap: "12px" }}
>
  {/* DELETE (×) */}
  {rows.length > 1 && (
    <button
      type="button"
      className="btn btn-danger btn-sm"
      onClick={() => removeRow(index)}
      title="Remove row"
    >
      ×
    </button>
  )}

  {/* ADD (+) → ONLY LAST ROW */}
  {index === rows.length - 1 && (
    <button
      type="button"
      className="btn btn-primary btn-sm"
      onClick={addRow}
      title="Add new row"
    >
      +
    </button>
  )}
</div>


              </div>
            ))}
          </div>

          {/* FOOTER */}
          <div className="card-footer text-end">
            <button
              type="submit"
              className="btn btn-primary px-4"
              disabled={!isFormValid || saving}
            >
              {saving ? "Saving..." : "Save"}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default ManualOT;
